import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent } from '../lib/analytics';
import { hasTrackedPurchase, markPurchaseTracked } from '../lib/checkout';
import './Shop.css';

function toSafeNumber(value, fallback = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return parsed;
}

function sanitizeAnalyticsItems(items) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => {
      const itemId = typeof item?.item_id === 'string' ? item.item_id.trim() : '';
      const itemName = typeof item?.item_name === 'string' ? item.item_name.trim() : '';
      const price = Math.max(0, toSafeNumber(item?.price, 0));
      const quantity = Math.max(1, Math.round(toSafeNumber(item?.quantity, 1)));

      if (!itemId && !itemName) {
        return null;
      }

      return {
        item_id: itemId || undefined,
        item_name: itemName || undefined,
        price,
        quantity
      };
    })
    .filter(Boolean);
}

function computeItemsTotal(items) {
  return items.reduce((total, item) => total + toSafeNumber(item?.price, 0) * toSafeNumber(item?.quantity, 0), 0);
}

const SuccessPage = ({ onCheckoutSuccess }) => {
  const hasHandledRef = useRef(false);
  const hasTrackedPurchaseRef = useRef(false);
  const [cartCleared, setCartCleared] = useState(false);

  useEffect(() => {
    if (hasHandledRef.current) {
      return;
    }

    hasHandledRef.current = true;
    const checkoutResult = onCheckoutSuccess?.() || null;
    const didClear = Boolean(checkoutResult?.didClear ?? checkoutResult);
    setCartCleared(didClear);

    if (hasTrackedPurchaseRef.current) {
      return;
    }

    const checkoutSessionId = checkoutResult?.checkoutSessionId || null;
    const pendingCheckout = checkoutResult?.pendingCheckout || null;
    const subtotalCents = Math.max(0, Math.round(toSafeNumber(pendingCheckout?.subtotalCents, 0)));
    const fallbackTransactionId = `success-${Date.now()}`;
    const transactionId = checkoutSessionId || pendingCheckout?.orderId || fallbackTransactionId;
    const hasPurchaseSignal = Boolean(checkoutSessionId || pendingCheckout || didClear);

    if (!hasPurchaseSignal || hasTrackedPurchase(transactionId)) {
      return;
    }

    const normalizedItems = sanitizeAnalyticsItems(checkoutResult?.items);
    const valueFromItems = computeItemsTotal(normalizedItems);
    const normalizedValue = Math.max(0, subtotalCents > 0 ? subtotalCents / 100 : valueFromItems);
    const purchaseItems =
      normalizedItems.length > 0
        ? normalizedItems
        : [
            {
              item_id: transactionId,
              item_name: 'Ordine online',
              price: normalizedValue,
              quantity: 1
            }
          ];

    trackEvent('purchase', {
      transaction_id: transactionId,
      currency: 'EUR',
      value: normalizedValue,
      items: purchaseItems
    });

    markPurchaseTracked(transactionId);
    hasTrackedPurchaseRef.current = true;
  }, [onCheckoutSuccess]);

  return (
    <section className="success-page">
      <h2 className="shop-title">Pagamento completato</h2>
      <p className="success-lead">Grazie, il tuo ordine e' stato ricevuto correttamente.</p>
      <p className="success-meta">Riceverai conferma e aggiornamenti spedizione tramite i recapiti inseriti al checkout.</p>
      {cartCleared && <p className="success-meta">Il carrello e' stato svuotato automaticamente.</p>}
      <div className="success-actions">
        <Link className="shop-back-link" to="/shop">
          Torna allo shop
        </Link>
        <Link className="shop-back-link" to="/">
          Torna al menu pizzeria
        </Link>
      </div>
    </section>
  );
};

export default SuccessPage;
